import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, Check, Loader2, Sparkles, Zap, History, Infinity } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const UpgradeModal = ({ isOpen, onClose, triggerContext = 'nav' }) => {
    const { isAuthenticated, getAuthHeaders } = useAuth();
    const [isLoading, setIsLoading] = useState(false);

    const handleUpgrade = async () => {
        if (!isAuthenticated) {
            toast.error('Please sign in to upgrade to Pro');
            onClose();
            return;
        }

        setIsLoading(true);
        try {
            const headers = getAuthHeaders();
            const originUrl = window.location.origin;
            
            const response = await axios.post(
                `${BACKEND_URL}/api/checkout/create-session`,
                { origin_url: originUrl },
                { headers }
            );
            
            if (response.data.url) {
                // Redirect to Stripe Checkout
                window.location.href = response.data.url;
            }
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to start checkout';
            toast.error(message);
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    const contextMessages = {
        nav: 'Unlock the full power of Vibe Coding Coach',
        limit: "You've reached today's free limit",
        continue: 'Save and continue building your app',
        generation: 'Want to keep building this idea?'
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0, y: 20 }}
                    className="w-full max-w-lg overflow-hidden relative z-10 rounded-2xl"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="upgrade-modal"
                >
                    {/* Gradient background */}
                    <div className="absolute inset-0 bg-gradient-to-br from-violet-900/50 via-surface to-fuchsia-900/30" />
                    
                    {/* Content */}
                    <div className="relative">
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 border-b border-white/10">
                            <div className="flex items-center gap-3">
                                <div className="p-3 rounded-xl bg-gradient-to-br from-violet-500/30 to-fuchsia-500/30">
                                    <Crown className="w-6 h-6 text-violet-300" />
                                </div>
                                <div>
                                    <h3 className="font-heading font-bold text-xl text-white">
                                        Upgrade to Pro
                                    </h3>
                                    <p className="text-sm text-gray-400">
                                        {contextMessages[triggerContext]}
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onClose}
                                className="hover:bg-white/10"
                            >
                                <X className="w-5 h-5" />
                            </Button>
                        </div>

                        {/* Pricing */}
                        <div className="p-6">
                            <div className="flex items-baseline gap-2 mb-6">
                                <span className="text-5xl font-bold text-white">$11</span>
                                <span className="text-lg text-gray-400">/month</span>
                            </div>

                            {/* Features */}
                            <div className="space-y-4 mb-8">
                                <div className="flex items-start gap-3">
                                    <div className="p-1.5 rounded-lg bg-violet-500/20">
                                        <Zap className="w-4 h-4 text-violet-400" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-white">30 blueprints per day</p>
                                        <p className="text-sm text-gray-400">10x more than the free plan</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-start gap-3">
                                    <div className="p-1.5 rounded-lg bg-violet-500/20">
                                        <Infinity className="w-4 h-4 text-violet-400" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-white">Unlimited saved blueprints</p>
                                        <p className="text-sm text-gray-400">Save and access all your work</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-start gap-3">
                                    <div className="p-1.5 rounded-lg bg-violet-500/20">
                                        <Sparkles className="w-4 h-4 text-violet-400" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-white">Continue & refine blueprints</p>
                                        <p className="text-sm text-gray-400">Add features, monetization, and more</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-start gap-3">
                                    <div className="p-1.5 rounded-lg bg-violet-500/20">
                                        <History className="w-4 h-4 text-violet-400" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-white">Full blueprint history</p>
                                        <p className="text-sm text-gray-400">Access all your past blueprints</p>
                                    </div>
                                </div>
                            </div>

                            {/* CTA Button */}
                            <Button
                                onClick={handleUpgrade}
                                disabled={isLoading}
                                className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 rounded-xl transition-all hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(139,92,246,0.4)]"
                                data-testid="upgrade-checkout-btn"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                        Starting Checkout...
                                    </>
                                ) : (
                                    <>
                                        <Crown className="w-5 h-5 mr-2" />
                                        Upgrade & Continue
                                    </>
                                )}
                            </Button>

                            {/* Cancel link */}
                            <button
                                onClick={onClose}
                                className="w-full mt-4 text-sm text-gray-400 hover:text-gray-300 transition-colors"
                            >
                                Maybe later
                            </button>

                            {/* Security note */}
                            <p className="text-xs text-gray-500 text-center mt-4">
                                Secure checkout powered by Stripe. Cancel anytime.
                            </p>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};
